// Ported from EnterpriseTemplates react-project-template (Release 1.3.1), PMS slice 4b.
// ============================================================================
// ORGANISATION MASTER SCREEN — the shared skeleton for entities, business units
// and financial years (the Org Admin module, onboarding standard D-5)
// ============================================================================
// Server-side paging and search over the projection (/api/v1/org/{kind});
// create and edit written through to the Tenant Hub organisation master; sync
// and verify against the master. Built on the enterprise shell: screen shell,
// standard confirms, action-button factory, useCan, EnterpriseFormModal.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory,
  type FormModalMode,
} from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, ColumnSelector, type ActionMenuItem } from 'components/ui';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useOrganisationList } from 'hooks/api/useOrganisation';
import { organisationService } from 'api/services/organisationService';
import type { OrganisationListParams, OrganisationLookups, OrganisationVerifyResult } from 'api/types/organisation.types';
import { OrganisationFormModal } from './OrganisationFormModal';
import { OrganisationViewModal } from './OrganisationViewModal';
import type { OrganisationRow, OrganisationScreenDefinition } from './organisationScreen.types';

export interface OrganisationMasterScreenProps<TRecord extends OrganisationRow, TForm extends object> {
  definition: OrganisationScreenDefinition<TRecord, TForm>;
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** CRS: the module's allowed actions from the layout (view / add / edit). */
  allowedActions?: string[];
}

/**
 * CRS stand-in for the template's useCan(screenCode) until the Centrix migration:
 * Centrix action codes mapped onto the CRS module actions the layout passes in.
 */
const CRS_ACTION_OF: Record<string, string> = { VIEW: 'view', CREATE: 'add', UPDATE: 'edit' };
const useCrsCan = (allowedActions: string[]) =>
  useCallback((action: string) => allowedActions.includes(CRS_ACTION_OF[action] ?? action.toLowerCase()), [allowedActions]);

const EMPTY_LOOKUPS: OrganisationLookups = { entityTypes: [], businessUnitTypes: [] };

const errorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message || err?.message || fallback;

/** The column selection saved for this screen, or null when none or unreadable. */
const readSavedColumns = (storageKey: string): string[] | null => {
  try {
    const saved = localStorage.getItem(storageKey);
    const parsed = saved ? JSON.parse(saved) : null;
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : null;
  } catch {
    return null;
  }
};

/** One line per difference, for the verify toast and the notice. */
export const describeVerify = (result: OrganisationVerifyResult): string => {
  if (result.ok) return `Projection matches the organisation master (version ${result.masterVersion}).`;
  const parts: string[] = [];
  if (result.missingLocally.length) parts.push(`${result.missingLocally.length} master row(s) missing here`);
  if (result.mismatched.length) parts.push(`${result.mismatched.length} field difference(s)`);
  if (result.localOnly.length) parts.push(`${result.localOnly.length} local-only row(s)`);
  if (result.orphanOrgRows) parts.push(`${result.orphanOrgRows} row(s) without a config scope`);
  return `Projection differs from the master: ${parts.join(', ')}. Run Sync.`;
};

export function OrganisationMasterScreen<TRecord extends OrganisationRow, TForm extends object>({
  definition, moduleIcon, tabId, moduleName, allowedActions = [],
}: OrganisationMasterScreenProps<TRecord, TForm>): React.ReactElement {
  const { kind, title, description, noun, columnDefinitions, gridColumns, recordCode, recordName, extraRowActions } = definition;
  const effectiveModuleName = moduleName || definition.moduleName;

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  // Template: useCan(definition.screenCode). CRS: module actions until Centrix.
  const can = useCrsCan(allowedActions);
  const { confirmCustom, ConfirmDialogPortal } = useStandardConfirms();

  // ---- Query (server-side paging + search) ----
  const [query, setQuery] = useState<OrganisationListParams>({ page: 1, pageSize: 25 });
  const { data, pagination, loading, refetch } = useOrganisationList<TRecord>(kind, query);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(prev => ({ ...prev, search: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // CRS (2026-10-22): business units filter by type, e.g. only the Departments.
  const [unitTypeCode, setUnitTypeCode] = useState('');
  const handleUnitTypeChange = useCallback((code: string) => {
    setUnitTypeCode(code);
    setQuery(prev => ({ ...prev, unitTypeCode: code || undefined, page: 1 }));
  }, []);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQuery(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQuery(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // ---- Lookups (type codes) — once per screen ----
  const [lookups, setLookups] = useState<OrganisationLookups>(EMPTY_LOOKUPS);
  useEffect(() => {
    let cancelled = false;
    organisationService.getLookups()
      .then(res => { if (!cancelled && res.success && res.data) setLookups(res.data); })
      .catch(() => { /* the form shows an empty type list; the API still validates */ });
    return () => { cancelled = true; };
  }, []);

  // ---- Columns ----
  // The actions column is structural: SyutGrid hides row actions unless 'actions' is in
  // visibleColumns, so it is prepended on init, apply and reset whatever the saved selection
  // holds (ENTERPRISE_SCREEN_CHECKLIST.md §4.1).
  const STRUCTURAL_COLUMN_IDS = ['actions'];
  const nonStructural = (cols: string[]) => cols.filter(c => !STRUCTURAL_COLUMN_IDS.includes(c));
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => [
    ...STRUCTURAL_COLUMN_IDS,
    ...nonStructural(readSavedColumns(definition.columnsStorageKey) ?? columnDefinitions.filter(c => c.defaultVisible).map(c => c.id)),
  ]);
  const handleApplyColumns = useCallback((cols: string[]) => {
    const next = [...STRUCTURAL_COLUMN_IDS, ...nonStructural(cols)];
    setVisibleColumns(next);
    try { localStorage.setItem(definition.columnsStorageKey, JSON.stringify(next)); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.columnsStorageKey]);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns([...STRUCTURAL_COLUMN_IDS, ...columnDefinitions.filter(c => c.defaultVisible && !STRUCTURAL_COLUMN_IDS.includes(c.id)).map(c => c.id)]);
    try { localStorage.removeItem(definition.columnsStorageKey); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefinitions, definition.columnsStorageKey]);

  // ---- Modals ----
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormModalMode>('create');
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState<TRecord | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');

  const findByCode = useCallback((code: string) => data.find(r => recordCode(r) === code) || null, [data, recordCode]);

  const handleRestoreModal = useCallback((mode: FormModalMode, instanceId?: string, code?: string) => {
    if (mode === 'create' && instanceId) setCreateInstanceId(instanceId);
    if (code) { const rec = findByCode(code); if (rec) setSelected(rec); }
    setFormMode(mode);
    setFormOpen(true);
  }, [findByCode]);

  const handleRestoreView = useCallback((code: string) => {
    const rec = findByCode(code);
    if (rec) { setSelected(rec); setViewOpen(true); }
  }, [findByCode]);

  const restoreIfMinimized = useCallback((modalId: string | null) => {
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return true;
    }
    return false;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  const handleView = useCallback((record: TRecord) => {
    if (restoreIfMinimized(tabId ? `${tabId}-view-modal-${recordCode(record)}` : null)) return;
    setSelected(record);
    setViewOpen(true);
  }, [tabId, recordCode, restoreIfMinimized]);

  const handleEdit = useCallback((record: TRecord) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-edit-${recordCode(record)}` : null)) return;
    setViewOpen(false);
    setFormMode('edit');
    setSelected(record);
    setFormOpen(true);
  }, [tabId, recordCode, restoreIfMinimized]);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create');
    setSelected(null);
    setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleSaved = useCallback(() => {
    setFormOpen(false);
    setSelected(null);
    refetch();
  }, [refetch]);

  // ---- Sync and verify (SUPER_ADMIN or IAM_ADMIN; the API decides) ----
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);

  const handleSync = useCallback(async () => {
    const ok = await confirmCustom({
      title: 'Sync with the organisation master',
      message: 'The projection is reconciled with the Tenant Hub organisation master: rows the master holds are inserted or updated here. Nothing is sent to the master.',
      confirmLabel: 'Sync',
    });
    if (!ok) return;
    try {
      const res = await organisationService.sync(false);
      if (!res.success || !res.data) throw new Error(res.message);
      const r = res.data;
      notificationService.toast.success(r.skipped
        ? `Already level with the master (version ${r.masterVersion}).`
        : `Synced to master version ${r.masterVersion}: ${r.inserted} inserted, ${r.updated} updated, ${r.unchanged} unchanged.`);
      setVerifyNotice(null);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(errorMessage(err, 'Sync failed.'));
    }
  }, [confirmCustom, refetch]);

  const handleVerify = useCallback(async () => {
    try {
      const res = await organisationService.verify();
      if (!res.success || !res.data) throw new Error(res.message);
      const text = describeVerify(res.data);
      setVerifyNotice(text);
      if (res.data.ok) notificationService.toast.success(text);
      else notificationService.toast.warning(text);
    } catch (err: any) {
      notificationService.toast.error(errorMessage(err, 'Verification failed.'));
    }
  }, []);

  // ---- Row actions ----
  const getRowActions = useCallback((record: TRecord): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(record) },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(record) });
    }
    if (extraRowActions) {
      items.push(...extraRowActions(record, { refetch, confirm: confirmCustom, canManage: can('UPDATE') }));
    }
    return items;
  }, [can, handleView, handleEdit, extraRowActions, refetch, confirmCustom]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, `Add ${noun}`), visible: can('CREATE') },
    { id: 'sync', label: 'Sync', icon: <IconSystem.download size={16} />, onClick: handleSync, tooltip: 'Reconcile with the organisation master', variant: 'secondary', visible: can('UPDATE') },
    { id: 'verify', label: 'Verify', icon: <IconSystem.checkCircle size={16} />, onClick: handleVerify, tooltip: 'Compare with the organisation master', variant: 'secondary', visible: can('VIEW') },
    actionButtonFactory.refresh(refetch),
  ];

  const headerIcon = useMemo(() => {
    const name = moduleIcon || definition.defaultIcon;
    const Icon = (IconSystem as any)[name];
    return Icon ? React.createElement(Icon, { size: 18 }) : undefined;
  }, [moduleIcon, definition.defaultIcon]);

  const gridData = useMemo(() => data as Array<TRecord & { id: string }>, [data]);

  return (
    <EnterpriseScreenShell
      title={title}
      description={description}
      icon={headerIcon}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: definition.searchPlaceholder }}
      customToolbarItems={
        <>
          {kind === 'business-units' && (
            <div style={{ minWidth: 180 }} data-field="unitTypeFilter">
              <SyutSelect
                options={lookups.businessUnitTypes.map(t => ({ value: t.typeCode, label: t.typeName }))}
                value={unitTypeCode}
                onChange={(v) => handleUnitTypeChange((v as string) || '')}
                placeholder="All types"
                isClearable
                size="sm"
                menuPortalTarget={document.body}
              />
            </div>
          )}
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        </>
      }
      trailingContent={verifyNotice && (
        <div role="status" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', padding: '0.25rem 0.5rem' }}>
          {verifyNotice}
        </div>
      )}
      ConfirmDialogPortal={ConfirmDialogPortal}
    >
      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0 0.25rem', flexShrink: 0 }}>
        {`${pagination.totalItems} ${pagination.totalItems === 1 ? noun.toLowerCase() : title.toLowerCase()}`}
      </div>

      <div className="data-grid-container">
        {loading && data.length === 0 ? (
          <GridLoadingSkeleton rows={10} columns={visibleColumns.length || 6} />
        ) : (
          <SyutGrid<TRecord & { id: string }>
            data={gridData}
            columns={gridColumns as any}
            isLoading={loading}
            enablePagination
            manualPagination
            totalCount={pagination.totalItems}
            currentPage={(query.page ?? 1) - 1}
            pageSize={query.pageSize ?? 25}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            visibleColumns={visibleColumns}
            emptyMessage={`No ${noun.toLowerCase()} records found`}
          />
        )}
      </div>

      <OrganisationFormModal<TRecord, TForm>
        definition={definition}
        lookups={lookups}
        isOpen={formOpen}
        mode={formMode}
        record={selected}
        tabId={tabId}
        moduleName={effectiveModuleName}
        createInstanceId={createInstanceId}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        onSaved={handleSaved}
        onRestoreModal={handleRestoreModal}
      />

      <OrganisationViewModal<TRecord, TForm>
        definition={definition}
        isOpen={viewOpen}
        record={selected}
        canEdit={can('UPDATE')}
        onClose={() => { setViewOpen(false); setSelected(null); }}
        onEdit={handleEdit}
        tabId={tabId}
        moduleName={effectiveModuleName}
        onRestoreView={handleRestoreView}
      />
    </EnterpriseScreenShell>
  );
}

export default OrganisationMasterScreen;
